import React, { useEffect, useState, useRef } from "react";
import { RefsObjectType } from "@/components/PageHome/interfaces";
import { DeviceInfo } from "@/interface/deviceInfo";
import IconLeft from "$/icons/icon-left.svg";
import styles from "./PageIndex.module.scss";

interface PageIndexComponentProps {
  data: { Heading: string; refId: string }[];
  userAgent: DeviceInfo;
  elementRefs: RefsObjectType;
  indexTitle?: string;
}

const PageIndexComponent: React.FC<PageIndexComponentProps> = ({ data, elementRefs, userAgent, indexTitle }) => {
  const contentListRef = useRef(null);
  
  const dataList = data?.filter(({ Heading }) => !!Heading);

  const [activeRefId, setActiveRefId] = useState<string>(dataList?.[0]?.refId);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const { isMobile } = userAgent;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry, index) => {
          if (entry.target instanceof HTMLElement && entry.isIntersecting) {
            if (entry.target?.dataset?.ref_id) {
              setActiveRefId(entry.target?.dataset?.ref_id);
            }
          }
        });
      },
      {
        root: null, // Use the viewport as the root
        threshold: 0.5, // Trigger as soon as any part of the element is visible
      }
    );

    // Observe all elements
    Object.values(elementRefs.current).forEach((element) => {
      if (element) observer.observe(element);
    });

    const contentListObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry, index) => {
          const ratio = entry.intersectionRatio;
          const boundingRect = entry.boundingClientRect;
          const intersectionRect = entry.intersectionRect;

          if (ratio === 0) {
            if (boundingRect.top < intersectionRect.top) {
              setShowMenu(true);
            } else {
              setShowMenu(false);
            }
          }
        });
      },
      {
        root: null, // Use the viewport as the root
        rootMargin: "0% 0% -100% 0%",
        threshold: 0, // Trigger as soon as any part of the element is visible
      }
    );

    if (contentListRef?.current) contentListObserver.observe(contentListRef.current);

    return () => {
      observer.disconnect(); // Cleanup observer
      contentListObserver.disconnect();
    };
  }, []);

  const handleMobileView: () => void = () => {
    setOpenMenu((prevShowMenu) => !prevShowMenu);
  };

  const handleLinkClick: (refId: string) => void = (refId) => {
    elementRefs?.current?.[refId]?.scrollIntoView({ behavior: "smooth" });
    setOpenMenu(false);
  };

  const activeLink = dataList?.find(({ refId }) => refId === activeRefId);

  return (
    <section className={`${isMobile ? "spacingBetweenSections" : ""}  container`}>
      <div ref={contentListRef} className={styles.wrapper}>
        <h5 className={styles.title}>On this page</h5>
        {dataList?.map(({ refId, Heading }) => {
          return (
            <div
              onClick={() => handleLinkClick(refId)}
              key={refId}
              className={`${styles.link} ${activeRefId === refId ? styles.active : ""}`}
              dangerouslySetInnerHTML={{ __html: Heading }}
            />
          );
        })}
      </div>
      {isMobile && (
        <div className={`${styles.stickyNavigation} ${showMenu ? styles.show : ""} ${openMenu ? styles.visible : ""}`}>
          <div className={styles.allSection}>
            {dataList?.map(({ refId, Heading }) => {
              return (
                <div
                  onClick={() => handleLinkClick(refId)}
                  key={refId}
                  className={`${styles.link}`}
                  dangerouslySetInnerHTML={{ __html: Heading }}
                />
              );
            })}
          </div>
          <div className={styles.currentSection} onClick={handleMobileView}>
            <div>
              <div className={styles.mobileTitle}>{indexTitle ? indexTitle : "On this page"}</div>
              {!!activeLink?.Heading && (
                <div
                  className={`${styles.active} ${styles.link}`}
                  dangerouslySetInnerHTML={{ __html: activeLink?.Heading }}
                />
              )}
            </div>
            <IconLeft height={24} width={24} />
          </div>
        </div>
      )}
    </section>
  );
};

export default PageIndexComponent;
